import React, { MouseEventHandler, ReactNode } from 'react';

// Components
import FormCheckbox, { FormCheckboxModifier } from '../../FormCheckbox/FormCheckbox';
import { FormLabelContainerModifier, FormLabelModifier } from '../../FormLabel/enumerable';
import FormSubmitOrCancel from '../../FormSubmitOrCancel/FormSubmitOrCancel';

import { useSonTranslation } from '../../../../../../translation/useSonTranslation';
import { StateFacetItem } from '../../../../ResultListSettings/ResultListSettings';
import { HiddenInput } from '../../../../../../_lib/Form2/elements/HiddenInput/HiddenInput';
import { HeadingHierarchy } from '../../../../../../_lib/components/Heading/Heading';

// Enumerable
const Modifier = {
  FACET: '--facet',
};

/**
 * Label's title can contain addtional info like a result count.
 * Parts of the sting is invisible (results).
 * @param item
 * @returns {JSX.Element|*}
 */
const getLabelTitle = (item: StateFacetItem): ReactNode | string => {
  if (item.additionalInformation) {
    return (
      <>
        {item.text} ({item.additionalInformation}
        <span className="invisible"> {item.invisibleLabelInformation}</span>)
      </>
    );
  }
  return item.text;
};

/**
 * Sometimes we want to return an alphabetically sorted array of items.
 * Backend can not sort here, because it sends us translation keys.
 * We sort the translated results.
 * @returns {*}
 */
const sortedItems = (items: StateFacetItem[], sortAlphabetically: boolean): StateFacetItem[] => {
  if (sortAlphabetically) {
    return items.sort((a, b) => a.text.localeCompare(b.text));
  }
  return items;
};

type FormMultiselectPanelType = {
  controls?: string;
  name: string;
  buttonHeadline?: string;
  hierarchy?: HeadingHierarchy;
  showButtons?: boolean;
  items?: StateFacetItem[];
  invertedButtons?: boolean;
  disableSubmitButton?: boolean;
  darkMode: boolean;
  // Events
  onSubmit: (event: any, activeItems: StateFacetItem[], name: string) => void;
  onChange: (event: any) => void;
  onReset: () => void;
  sortAlphabetically?: boolean;
};

/**
 * FormMultiselect
 */
export default function FormMultiselectPanel({
  controls,
  name,
  buttonHeadline,
  hierarchy,
  showButtons = false,
  items = [],
  invertedButtons = false,
  disableSubmitButton = false,
  darkMode,
  onSubmit,
  onChange,
  onReset,
  sortAlphabetically = false,
}: FormMultiselectPanelType) {
  const { t } = useSonTranslation('FormMultiselectPanel');

  /**
   * Renders a single Item
   * The item contains a Checkbox and a Label
   */
  const renderSingleListItem = (item: StateFacetItem, index: number) => {
    const disabled = item.readOnly === true || item.assignable === false;
    return (
      <li className="form-multiselect-panel__list-item" key={`checkbox-${item.value}`}>
        <FormCheckbox
          id={`checkbox-${index.toString()}`}
          name={name}
          modifier={FormCheckboxModifier.HEIGHT100}
          modifierLabel={FormLabelModifier.CHECKBOX_MULTISELECT}
          modifierLabelContainer={FormLabelContainerModifier.FLEX100}
          checked={item.active}
          value={item.value}
          labelTitle={getLabelTitle(item)}
          disabled={disabled}
          // Events
          onChange={onChange}
        />
        {
          // - HTML doesn't submit any value of unchecked checkboxes.
          // - HTML doesn't submit any value of disabled checkboxes (checked or unchecked).
          // - HTML doesn't know readonly attribute for checkboxes.
          // => We send an additional hidden input instead for disabled but checked checkboxes.
          disabled && item.active && <HiddenInput name={name} value={item.value} />
        }
      </li>
    );
  };

  const getClassNameFacet = (className: string) =>
    darkMode ? `${className} ${className + Modifier.FACET}` : className;

  return (
    <div id={controls} className={getClassNameFacet('form-multiselect-panel')}>
      <div className={getClassNameFacet('form-multiselect-panel__container')}>
        {items && items.length > 0 && (
          <div className="form-multiselect-panel__scrolling-box">
            <ol className="form-multiselect-panel__list">
              {sortedItems(items, sortAlphabetically).map(renderSingleListItem)}
            </ol>{' '}
          </div>
        )}
        {showButtons && buttonHeadline && hierarchy && (
          <div className={getClassNameFacet('form-multiselect-panel__interactions')}>
            <FormSubmitOrCancel
              acceptButtonText={t('buttons.submit')}
              acceptButtonTitle={t('buttons.submit')}
              cancelButtonText={t('buttons.cancel')}
              cancelButtonTitle={t('buttons.cancel')}
              acceptAction={onSubmit as MouseEventHandler<HTMLButtonElement>}
              cancelAction={onReset}
              headline={buttonHeadline}
              hierarchy={hierarchy}
              hideHeadline
              invertedButtons={invertedButtons}
              disableSubmitButton={disableSubmitButton}
            />
          </div>
        )}
      </div>
    </div>
  );
}
